import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Button,
  Input,
  Select,
  Table,
  Drawer,
  Modal,
  message,
  AutoComplete,
  Row,
  Col,
  Divider,
  Spin,
} from 'antd';
import { MagnifyingGlass, Check, Columns } from '@phosphor-icons/react';
import { ref, get, update } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useFirebase } from '../FirebaseContext';
import moment from 'moment';
import './Rozliczenie.css'; // Подключаем стили

const { Option } = Select;

/**
 * Komponent: Rozliczenie
 * ---------------------------------------
 * • szybkie pobieranie danych (asyncPool + cache)
 * • filtr w useMemo – mniej setState / renderów
 * • nie ściąga całej gałęzi „drivers", tylko summary wybranego tygodnia
 */
const Rozliczenie = () => {
  /* -------------------- state -------------------- */
  const [searchValue, setSearchValue] = useState('');
  const [city, setCity] = useState('');
  const [week, setWeek] = useState('');
  const [cities, setCities] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [drivers, setDrivers] = useState([]);        // [{ value: driverId }]
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [isApproveDisabled, setIsApproveDisabled] = useState(true);
  const [isProjectChanging, setIsProjectChanging] = useState(false);

  /* --------------- Firebase refs --------------- */
  const { currentFirebase } = useFirebase();
  const database = currentFirebase.database;
  const prevDbRef = useRef(null);          // wykrywa zmianę projektu

  /* --------------- cache tygodni --------------- */
  const weekCache = useRef(new Map());     // Map<weekKey, Array<summary>>

  /* --------------- utils ----------------------- */
  const formatNumber = (num) => Number(num).toFixed(2);

  // Функция для форматирования денежных значений с цветовой кодировкой
  const formatMoney = (value, isTotal = false, isNegativeRed = false) => {
    const numValue = parseFloat(value);
    let className = 'rozliczenie-money-neutral';
    
    if (numValue > 0) {
      className = 'rozliczenie-money-positive';
    } else if (numValue < 0) {
      className = 'rozliczenie-money-negative';
    } else if (numValue === 0) {
      className = 'rozliczenie-money-zero';
    }
    
    // Для специальных колонок (Wynajem, ZUS, Inne) - красный цвет
    if (isNegativeRed && numValue !== 0) {
      className = 'rozliczenie-money-negative';
    }
    
    if (isTotal) {
      className += ' rozliczenie-total-column';
    }
    
    return (
      <span className={`rozliczenie-number ${className}`} style={{ fontSize: '14px', fontWeight: isTotal ? '600' : '500' }}>
        {formatNumber(numValue)} PLN
      </span>
    );
  };

  // Функция для форматирования статуса
  const formatStatus = (status) => {
    let className = 'rozliczenie-status ';
    
    switch (status) {
      case 'Czekam na zatwierdzenie':
        className += 'rozliczenie-status-waiting';
        break;
      case 'Rozliczenie dodane':
        className += 'rozliczenie-status-approved';
        break;
      default:
        className += 'rozliczenie-status-default';
    }
    
    return <span className={className} style={{ fontSize: '13px', padding: '4px 8px' }}>{status}</span>;
  };

  /** asynchroniczny pool – maksymalnie `limit` równoległych zapytań */
  const asyncPool = async (limit, array, iteratorFn) => {
    const ret = [];
    const executing = [];
    for (const item of array) {
      const p = Promise.resolve().then(() => iteratorFn(item));
      ret.push(p);
      if (limit <= array.length) {
        const e = p.then(() => executing.splice(executing.indexOf(e), 1));
        executing.push(e);
        if (executing.length >= limit) await Promise.race(executing);
      }
    }
    return Promise.all(ret);
  };

  /* --------------- pobieranie list (Cloud Function — без загрузки /drivers на клиент) ---------------- */
  const getWeeksAndDrivers = async () => {
    try {
      const functions = getFunctions(currentFirebase.app, 'europe-west1');
      const fn = httpsCallable(functions, 'getWeeksAndDriverIds');
      const result = await fn();
      return { weeks: result.data.weeks, drivers: result.data.drivers };
    } catch (e) {
      console.error('weeks+drivers via CF', e);
      return { weeks: [], drivers: [] };
    }
  };

  // Обратная совместимость
  const getDriversList = async () => {
    try {
      const snap = await get(ref(database, 'drivers'));
      return Object.keys(snap.val() || {}).map((id) => ({ value: id }));
    } catch (e) {
      console.error('drivers list', e);
      return [];
    }
  };

  /* --------------- główne ładowanie projektu --------------- */
  useEffect(() => {
    const firstLoad = async () => {
      let showOverlay = false;
      if (prevDbRef.current && prevDbRef.current !== database) {
        showOverlay = true;
        setIsProjectChanging(true);
      }

      setLoading(true);
      try {
        // Один запрос к Firebase вместо двух
        const { weeks: wk, drivers: drv } = await getWeeksAndDrivers();
        setWeeks(wk);
        setDrivers(drv);
        if (wk.length) {
          setWeek(wk[0]);
          await loadAndDisplayWeekData(wk[0], drv);
        }
      } finally {
        setLoading(false);
        prevDbRef.current = database;
        if (showOverlay) setIsProjectChanging(false);
      }
    };
    firstLoad();
  }, [database]);

  /* --------------- szybkie ładowanie tygodnia --------------- */
  const loadAndDisplayWeekData = async (selectedWeek, driverList = drivers) => {
    // z cache
    if (weekCache.current.has(selectedWeek)) {
      const cached = weekCache.current.get(selectedWeek);
      setData(cached);
      setCities([...new Set(cached.map((d) => d.city))].sort());
      return;
    }

    setLoading(true);
    try {
      const ids = driverList.length ? driverList.map((d) => d.value) : (await getDriversList()).map((d) => d.value);
      const fetched = [];
      const citySet = new Set();

      await asyncPool(10, ids, async (id) => {
        const snap = await get(ref(database, `drivers/${id}/weeks/${selectedWeek}/summary`));
        if (!snap.exists()) return;
        const s = snap.val();
        citySet.add(s.city || 'N/A');
        fetched.push({
          driverId: id,
          city: s.city || 'N/A',
          kursy: formatNumber(s.kursy || 0),
          przychod_dodatkowy: formatNumber(s.przychod_dodatkowy || 0),
          commission: formatNumber(s.commission || 0),
          gotowka: formatNumber(s.gotowka || 0),
          partner: s.partner || 'N/A',
          service: Array.isArray(s.service) ? s.service.join(', ') : 'N/A',
          vat_bonus: formatNumber(s.vat_bonus || 0),
          vat_dodatkowy: formatNumber(s.vat_dodatkowy || 0),
          vat_przejazdy: formatNumber(s.vat_przejazdy || 0),
          wynajem: formatNumber(s.wynajem || 0),
          zus: formatNumber(s.zus || 0),
          koztyUZ: formatNumber(s.koztyUZ || 0),
          inne: formatNumber(s.inne || 0),
          bonusPartnera: formatNumber(s.bonusPartnera || 0),
          umowa_najmu: formatNumber(s.umowa_najmu || 0),
          zwrot_kosztow: formatNumber(s.zwrot_kosztow || 0),
          podatek_do_zaplaty: formatNumber(s.podatek_do_zaplaty || 0),
          total: formatNumber(s.total || 0),
          status: s.status || 'N/A',
          // Детализированные поля Uber
          korekta: formatNumber(s.korekta || 0),
          uberx_priority: formatNumber(s.uberx_priority || 0),
          czas_oczekiwania: formatNumber(s.czas_oczekiwania || 0),
          program_quest: formatNumber(s.program_quest || 0),
          napiwek: formatNumber(s.napiwek || 0),
          mnoznik: formatNumber(s.mnoznik || 0),
          uber_pets: formatNumber(s.uber_pets || 0),
          odwolanie: formatNumber(s.odwolanie || 0),
          oplata_drogowa: formatNumber(s.oplata_drogowa || 0),
          oplata_rezerwacja: formatNumber(s.oplata_rezerwacja || 0),
          zwrot_zagubiony_przedmiot: formatNumber(s.zwrot_zagubiony_przedmiot || 0),
          czyszczenie_naprawy: formatNumber(s.czyszczenie_naprawy || 0),
          dodatkowa_oplata_odwolanie: formatNumber(s.dodatkowa_oplata_odwolanie || 0),
          promocja: formatNumber(s.promocja || 0),
          premia_motywacyjna: formatNumber(s.premia_motywacyjna || 0),
          nagroda_polecenie: formatNumber(s.nagroda_polecenie || 0),
          gwarancja_nowi_kierowcy: formatNumber(s.gwarancja_nowi_kierowcy || 0),
          boost_plus: formatNumber(s.boost_plus || 0),
          inne_korekty: formatNumber(s.inne_korekty || 0),
          oplata_lotniskowa: formatNumber(s.oplata_lotniskowa || 0),
          // Детализированные поля Freenow
          freenow_incentives: formatNumber(s.freenow_incentives || 0),
          freenow_tolls: formatNumber(s.freenow_tolls || 0),
          freenow_cancellations: formatNumber(s.freenow_cancellations || 0),
          freenow_waiting_time: formatNumber(s.freenow_waiting_time || 0),
          freenow_tips: formatNumber(s.freenow_tips || 0),
          freenow_airport_fee: formatNumber(s.freenow_airport_fee || 0),
          freenow_hotel_fee: formatNumber(s.freenow_hotel_fee || 0),
        });
      });

      weekCache.current.set(selectedWeek, fetched);
      setData(fetched);
      setCities(Array.from(citySet).sort());
    } catch (e) {
      console.error('week data', e);
    } finally {
      setLoading(false);
    }
  };

  /* --------------- filtr (useMemo) ---------------- */
  const filtered = useMemo(() => {
    return data.filter((item) => {
      const matchSearch = item.driverId.toLowerCase().includes(searchValue.toLowerCase());
      const matchCity = city === '' || item.city === city;
      return matchSearch && matchCity;
    });
  }, [data, searchValue, city]);

  useEffect(() => {
    setFilteredData(filtered);
    const hasAwait = filtered.some((i) => i.status === 'Czekam na zatwierdzenie');
    setIsApproveDisabled(!hasAwait);
  }, [filtered]);

  /* --------------- approve ----------------------- */
  const chunkArray = (arr, size) => arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);

  const handleApprove = async () => {
    if (!week) return message.error('Proszę wybrać tydzień.');
    const todo = filtered.filter((i) => i.status === 'Czekam na zatwierdzenie');
    if (!todo.length) return message.info('Brak wierszy do aktualizacji.');

    setLoading(true);
    try {
      for (const chunk of chunkArray(todo, 50)) {
        const upd = {};
        chunk.forEach(({ driverId }) => {
          upd[`drivers/${driverId}/weeks/${week}/summary/status`] = 'Rozliczenie dodane';
        });
        await update(ref(database), upd);
      }
      message.success('Statusy zaktualizowane!');
      await loadAndDisplayWeekData(week); // odśwież
    } catch (e) {
      console.error('approve', e);
      message.error('Błąd podczas aktualizacji statusów.');
    } finally {
      setLoading(false);
    }
  };

  /* --------------- колонки таблицы (мемоизированы) ---------------- */
  const basicColumns = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'driverId',
      key: 'driverId',
      sorter: (a, b) => a.driverId.localeCompare(b.driverId),
      width: 120,
      fixed: 'left',
      render: (id) => <span className="rozliczenie-driver-id">{id}</span>
    },
    { 
      title: <span>Kursy</span>, 
      dataIndex: 'kursy', 
      key: 'kursy', 
      sorter: (a, b) => parseFloat(a.kursy) - parseFloat(b.kursy),
      width: 100,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Przychód dodatkowy</span>, 
      dataIndex: 'przychod_dodatkowy', 
      key: 'przychod_dodatkowy', 
      sorter: (a, b) => parseFloat(a.przychod_dodatkowy) - parseFloat(b.przychod_dodatkowy),
      width: 160,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Gotówka</span>, 
      dataIndex: 'gotowka', 
      key: 'gotowka', 
      sorter: (a, b) => parseFloat(a.gotowka) - parseFloat(b.gotowka),
      width: 120,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Bonus VAT</span>, 
      dataIndex: 'vat_bonus', 
      key: 'vat_bonus', 
      sorter: (a, b) => parseFloat(a.vat_bonus) - parseFloat(b.vat_bonus),
      width: 120,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Dodatkowy VAT</span>, 
      dataIndex: 'vat_dodatkowy', 
      key: 'vat_dodatkowy', 
      sorter: (a, b) => parseFloat(a.vat_dodatkowy) - parseFloat(b.vat_dodatkowy),
      width: 140,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>VAT przejazdy</span>, 
      dataIndex: 'vat_przejazdy', 
      key: 'vat_przejazdy', 
      sorter: (a, b) => parseFloat(a.vat_przejazdy) - parseFloat(b.vat_przejazdy),
      width: 130,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Wynajem</span>, 
      dataIndex: 'wynajem', 
      key: 'wynajem', 
      sorter: (a, b) => parseFloat(a.wynajem) - parseFloat(b.wynajem),
      width: 120,
      align: 'right',
      render: (value) => formatMoney(value, false, true)
    },
    { 
      title: <span>ZUS</span>, 
      dataIndex: 'zus', 
      key: 'zus', 
      sorter: (a, b) => parseFloat(a.zus) - parseFloat(b.zus),
      width: 100,
      align: 'right',
      render: (value) => formatMoney(value, false, true)
    },
    { 
      title: <span>Inne</span>, 
      dataIndex: 'inne', 
      key: 'inne', 
      sorter: (a, b) => parseFloat(a.inne) - parseFloat(b.inne),
      width: 100,
      align: 'right',
      render: (value) => formatMoney(value, false, true)
    },
    { 
      title: <span>Partner</span>, 
      dataIndex: 'partner', 
      key: 'partner',
      width: 120,
      align: 'right',
      render: (partner) => {
        // Преобразуем в число если это возможно, иначе считаем как 0
        const numValue = parseFloat(partner) || 0;
        return formatMoney(numValue, false, true);
      }
    },
    {
      title: 'Do wypłaty',
      dataIndex: 'total',
      key: 'total',
      sorter: (a, b) => parseFloat(a.total) - parseFloat(b.total),
      width: 130,
      align: 'right',
      render: (value) => formatMoney(value, true)
    },
  ], []);

  const additionalColumns = useMemo(() => [
    { 
      title: <span>Miasto</span>, 
      dataIndex: 'city', 
      key: 'city',
      width: 120,
      render: (city) => (
        <span style={{ 
          fontSize: '13px',
          backgroundColor: '#f6ffed',
          padding: '4px 8px',
          borderRadius: '4px',
          color: '#52c41a',
          fontWeight: '500'
        }}>
          {city}
        </span>
      )
    },
    { 
      title: <span>Usługa</span>, 
      dataIndex: 'service', 
      key: 'service',
      width: 180,
      render: (service) => (
        <span style={{ fontSize: '13px', color: '#595959' }}>
          {service}
        </span>
      )
    },
    { 
      title: <span>Koszty UZ</span>, 
      dataIndex: 'koztyUZ', 
      key: 'koztyUZ', 
      sorter: (a, b) => parseFloat(a.koztyUZ) - parseFloat(b.koztyUZ),
      width: 120,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Bonus partnera</span>, 
      dataIndex: 'bonusPartnera', 
      key: 'bonusPartnera', 
      sorter: (a, b) => parseFloat(a.bonusPartnera) - parseFloat(b.bonusPartnera),
      width: 140,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Umowa najmu</span>, 
      dataIndex: 'umowa_najmu', 
      key: 'umowa_najmu', 
      sorter: (a, b) => parseFloat(a.umowa_najmu) - parseFloat(b.umowa_najmu),
      width: 140,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Zwrot kosztów</span>, 
      dataIndex: 'zwrot_kosztow', 
      key: 'zwrot_kosztow', 
      sorter: (a, b) => parseFloat(a.zwrot_kosztow) - parseFloat(b.zwrot_kosztow),
      width: 140,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Podatek do zapłaty</span>, 
      dataIndex: 'podatek_do_zaplaty', 
      key: 'podatek_do_zaplaty', 
      sorter: (a, b) => parseFloat(a.podatek_do_zaplaty) - parseFloat(b.podatek_do_zaplaty),
      width: 160,
      align: 'right',
      render: (value) => formatMoney(value)
    },
    { 
      title: <span>Status</span>, 
      dataIndex: 'status', 
      key: 'status',
      width: 170,
      render: (status) => formatStatus(status)
    },
  ], []);

  const columnsToShow = useMemo(
    () => showAllColumns ? [...basicColumns, ...additionalColumns] : basicColumns,
    [showAllColumns, basicColumns, additionalColumns]
  );

  /* --------------- szczegóły modal ---------------- */
  const showDetailsModal = async (driverId) => {
    setSelectedDriverId(driverId);
    setModalVisible(true);
    setModalContent(
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
    
    try {
      const [appsSnap, sumSnap] = await Promise.all([
        get(ref(database, `drivers/${driverId}/weeks/${week}/apps`)),
        get(ref(database, `drivers/${driverId}/weeks/${week}/summary`)),
      ]);
      const apps = appsSnap.val();
      const summary = sumSnap.val();

      // Компактный вывод приложений
      const rows = apps ? Object.entries(apps).map(([appName, det]) => {
        const fields = [
          { label: 'Miasto', value: det.city || 'N/A' },
          { label: 'Kursy', value: formatNumber(det.kursy || 0) + ' PLN', highlight: true },
          { label: 'Przychód dod.', value: formatNumber(det.przychod_dodatkowy || 0) + ' PLN' },
          { label: 'Gotówka', value: formatNumber(det.gotowka || 0) + ' PLN' },
          { label: 'Prowizja', value: formatNumber(det.commission || 0) + ' PLN' },
          { label: 'VAT przejazdy', value: formatNumber(det.vat_przejazdy || 0) + ' PLN' },
          { label: 'Okres', value: det.period_start && det.period_end ? `${det.period_start} → ${det.period_end}` : '-' },
        ];
        return (
          <div key={appName} className="rz-app-section">
            <div className="rz-app-header">
              <span className="rz-app-name">{appName}</span>
              <span className={`rozliczenie-status ${
                det.status === 'Rozliczenie dodane' ? 'rozliczenie-status-approved'
                : det.status === 'Czekam na zatwierdzenie' ? 'rozliczenie-status-waiting'
                : 'rozliczenie-status-default'
              }`}>{det.status || 'N/A'}</span>
            </div>
            <div className="rz-app-grid">
              {fields.map((f, i) => (
                <div key={i} className="rz-app-field">
                  <span className="rz-field-label">{f.label}</span>
                  <span className={`rz-field-value${f.highlight ? ' highlight' : ''}`}>{f.value}</span>
                </div>
              ))}
              <div className="rz-app-field total">
                <span className="rz-field-label">Total</span>
                <span className="rz-field-value highlight">{formatNumber(det.total || 0)} PLN</span>
              </div>
            </div>
          </div>
        );
      }) : [];

      setModalContent(
        <div>
          {rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              Brak danych dla wybranego tygodnia
            </div>
          ) : rows}
        </div>
      );
    } catch (e) {
      console.error('details', e);
      setModalContent(
        <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>
          Błąd: {e.message}
        </div>
      );
    }
  };
  /* -------------------------- JSX -------------------------- */
  return (
    <div>
      {/* Toolbar */}
      <div className="rozliczenie-toolbar">
        <Input
          prefix={<MagnifyingGlass size={16} color="#9ca3af" />}
          placeholder="Szukaj kierowcę..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          allowClear
          style={{ flex: 2, minWidth: 150 }}
        />
        <AutoComplete
          options={cities.map((c) => ({ value: c }))}
          placeholder="Miasto"
          value={city}
          onChange={setCity}
          allowClear
          style={{ flex: 1, minWidth: 120 }}
        />
        <Select
          value={week}
          onChange={(val) => { setWeek(val); loadAndDisplayWeekData(val); }}
          style={{ flex: 1, minWidth: 130 }}
          placeholder="Tydzień"
        >
          {weeks.map((w) => <Option key={w} value={w}>{w}</Option>)}
        </Select>
        <Button
          onClick={() => setShowAllColumns((p) => !p)}
          style={{ flexShrink: 0 }}
        >
          <Columns size={15} weight="bold" /> {showAllColumns ? 'Mniej' : 'Więcej'}
        </Button>
        <Button
          type="primary"
          onClick={handleApprove}
          disabled={isApproveDisabled}
          loading={loading}
          style={{ flexShrink: 0 }}
        >
          <Check size={15} weight="bold" /> Zatwierdź
        </Button>
      </div>

      <Table
        className="rozliczenie-table"
        columns={columnsToShow}
        dataSource={filteredData}
        loading={loading}
        rowKey="driverId"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: currentPage,
          pageSize: rowsPerPage,
          total: filtered.length,
          onChange: setCurrentPage,
          showSizeChanger: true,
          pageSizeOptions: ['15', '25', '50'],
          onShowSizeChange: (_, size) => setRowsPerPage(size),
          showTotal: (total, range) => `${range[0]}–${range[1]} z ${total}`,
          size: 'small',
        }}
        size="middle"
        onRow={(record) => ({
          onClick: () => showDetailsModal(record.driverId),
          style: { cursor: 'pointer' },
        })}
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{selectedDriverId}</span>
            <span style={{ color: '#9ca3af', fontSize: 13 }}>tydzień {week}</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        centered
        className="rozliczenie-modal"
      >
        {modalContent}
      </Modal>

      {isProjectChanging && (
        <div className="rozliczenie-project-overlay">
          <Spin size="large" />
          <div className="rozliczenie-project-overlay-text" style={{ fontSize: '16px', marginTop: '16px' }}>
            🔄 Przełączam projekt…
          </div>
        </div>
      )}
    </div>
  );
};

export default Rozliczenie;